import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronsUpDown, Check, Flag, Mountain, Trophy } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useGymAdmin } from '../context/gym-admin-context';
import { staffLocationType, type StaffLocationType, type UserStaff } from '../../types/user';

const CONTENT_CLASS =
  'z-50 max-h-[70svh] w-[--radix-dropdown-menu-trigger-width] min-w-56 overflow-y-auto rounded-lg border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0';

const ITEM_CLASS =
  'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground';

/** The mark that stands for each kind of place when there is no logo. */
const FALLBACK_ICONS: Record<StaffLocationType, typeof Building2> = {
  gym: Building2,
  crag: Mountain,
  trophy: Trophy,
  competition: Flag,
  location: Building2,
};

/**
 * The place's own logo, falling back to a generic mark for one that has never
 * uploaded any. It is the whole of the header once the sidebar is collapsed, so
 * it has to read as the place on its own — which is why the fallback tells the
 * kinds apart: with several of each and no logos, the mark is all there is.
 */
function GymLogo({ gym, className }: { gym: UserStaff | null; className?: string }) {
  const FallbackIcon = FALLBACK_ICONS[staffLocationType(gym)] ?? Building2;

  return (
    <div
      className={cn(
        'flex aspect-square size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-sidebar-primary text-sidebar-primary-foreground',
        className
      )}
      data-testid="gym-logo"
    >
      {gym?.gym_logo_small_url ? (
        <img
          src={gym.gym_logo_small_url}
          alt={gym.gym_name}
          className="size-full object-cover"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <FallbackIcon className="size-4" aria-hidden />
      )}
    </div>
  );
}

/** One row of the dropdown. Stages are indented under the circuit they belong to. */
function PlaceItem({
  place,
  nested,
  selected,
  onSelect,
}: {
  place: UserStaff;
  nested?: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      className={cn(ITEM_CLASS, nested && 'pl-6')}
      onSelect={onSelect}
      data-testid={nested ? 'switcher-stage' : 'switcher-place'}
    >
      <GymLogo gym={place} className="size-6 rounded-md bg-transparent text-foreground" />
      <span className="flex-1 truncate">{place.gym_name}</span>
      <Check className={cn('size-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
    </DropdownMenuItem>
  );
}

/** A place with the competitions filed under it. */
interface PlaceGroup {
  place: UserStaff;
  children: UserStaff[];
}

/**
 * The list as it is read: each gym with the events it holds indented under it,
 * then the crags, then each circuit with its stages under it, then any
 * competition whose gym or circuit the user cannot manage.
 *
 * The nesting is the point. A trophy admin sees every stage of theirs in this
 * list, and half a dozen "Tappa 1"s loose among the gyms would be unreadable —
 * under their circuit, in date order, they are obvious. A gym's events read
 * the same way under the gym. `parent_slug` is the place the user reaches a
 * competition through, so each is filed once.
 */
function useGroupedPlaces(places: UserStaff[]) {
  return useMemo(() => {
    const typeOf = (p: UserStaff) => staffLocationType(p);
    // Archived circuits and competitions are left out of the list — that is
    // most of what archiving one is for — but not out of `staffGyms`: their
    // panel still answers by URL, which is the only way anybody can take a
    // season back out of the archive.
    const current = places.filter((p) => p.location_status !== 'archived');
    const byType = (type: StaffLocationType) => current.filter((p) => typeOf(p) === type);

    const competitions = byType('competition');
    const under = (parents: UserStaff[]): PlaceGroup[] =>
      parents.map((place) => ({
        place,
        children: competitions.filter((c) => c.parent_slug === place.gym_slug),
      }));

    const gyms = under(byType('gym'));
    const crags = under(byType('crag'));
    const circuits = under(byType('trophy'));
    const filed = new Set(
      [...gyms, ...crags, ...circuits].flatMap((group) => group.children.map((c) => c.gym_slug))
    );

    return {
      gyms,
      crags,
      circuits,
      // A competition whose gym or circuit is not in the list: staff on it alone.
      loose: competitions.filter((c) => !filed.has(c.gym_slug)),
    };
  }, [places]);
}

/**
 * Place picker rendered at the top of the sidebar. Lets a staff member switch
 * between everything they manage; the choice is persisted by the context.
 *
 * Collapsed to the rail there is only room for the logo, so the name and the
 * chevron drop away and the logo itself becomes the trigger.
 */
export function GymSwitcher() {
  const { t } = useTranslation();
  const { staffGyms, currentGym, selectGym } = useGymAdmin();
  const { gyms, crags, circuits, loose } = useGroupedPlaces(staffGyms);

  const isSelected = (place: UserStaff) => place.gym_slug === currentGym?.gym_slug;

  // One header, then each place with whatever competitions hang under it.
  const group = (labelKey: string, groups: PlaceGroup[]) =>
    groups.length > 0 && (
      <div key={labelKey}>
        <DropdownMenuLabel className="px-2 py-1 text-xs text-muted-foreground">
          {t(labelKey)}
        </DropdownMenuLabel>
        {groups.map(({ place, children }) => (
          <div key={place.gym_slug}>
            <PlaceItem
              place={place}
              selected={isSelected(place)}
              onSelect={() => selectGym(place.gym_slug)}
            />
            {children.map((child) => (
              <PlaceItem
                key={child.gym_slug}
                place={child}
                nested
                selected={isSelected(child)}
                onSelect={() => selectGym(child.gym_slug)}
              />
            ))}
          </div>
        ))}
      </div>
    );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* `title` rather than the button's own tooltip: that one wraps the
                button in a TooltipTrigger, which would swallow the props this
                dropdown trigger has to hand down. */}
            <SidebarMenuButton
              size="lg"
              title={currentGym?.gym_name ?? t('gymAdmin.switcher.none')}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <GymLogo gym={currentGym} />
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold">
                  {currentGym?.gym_name ?? t('gymAdmin.switcher.none')}
                </span>
                <span className="truncate text-xs text-sidebar-foreground/70">
                  {t('gymAdmin.switcher.label')}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={CONTENT_CLASS} align="start" sideOffset={4}>
            {group('gymAdmin.switcher.groups.gyms', gyms)}
            {group('gymAdmin.switcher.groups.crags', crags)}
            {group('gymAdmin.switcher.groups.trophies', circuits)}
            {group(
              'gymAdmin.switcher.groups.stages',
              loose.map((place) => ({ place, children: [] }))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
